import { compact } from "lodash";
import React from "react";
import { useLocation } from "react-router-dom";
import { TFLocationState } from "types/topicflow";

import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import {
  currentOrganizationVar,
  leftSidePanelVar,
  successNotificationVar,
} from "@cache/cache";
import Dropdown from "@components/dropdown/dropdown";
import { DropdownOptionType } from "@components/dropdown/dropdown-types";
import { useLink } from "@components/link/link-helpers";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconType } from "@components/topicflow-icon/topicflow-icon-helpers";
import client from "@graphql/client";
import { chromeExtensionUrl } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { toWithBackground } from "@helpers/helpers";

import { apolloPersistCacheKey } from "../../../helpers/constants";
import { isInternalImpersonation } from "./sidebar-hijack-helpers";
import { sidebarButtonClassName } from "./sidebar-navigation";

type Props = {
  menuIsCollapsed?: boolean;
};

const SidebarSettings: React.FC<Props> = ({ menuIsCollapsed }) => {
  const location = useLocation<TFLocationState>();
  const organization = currentOrganizationVar();
  const { saveUiPreference } = useUiPreferenceCache();
  const link = useLink();

  const clearCache = () => {
    client.resetStore();
    client.clearStore();
    localStorage.removeItem(apolloPersistCacheKey);
  };

  const handleClearCache = () => {
    clearCache();
    successNotificationVar({ title: "Cache cleared" });
  };

  const handleLogout = () => {
    clearCache();
    window.location.href = "/logout";
    if (window.Intercom) {
      window.Intercom("shutdown");
    }
  };

  const openSettingsAndNavigate = (path: string) => {
    saveUiPreference({
      leftNavigationSidebarExpanded: true,
    });
    leftSidePanelVar("settings");
    link.redirect(path);
  };

  const handleOpenSettings = () => {
    openSettingsAndNavigate("/account/profile");
  };

  const menuIcon = (type: TopicflowIconType) => (
    <TopicflowIcon type={type} className="h-4 w-4" />
  );

  const options = compact([
    {
      type: "link",
      label: "Invite People",
      icon: menuIcon("userPlus02"),
      to: toWithBackground({
        pathname: "/invite",
        location,
      }),
    },
    {
      type: "button",
      label: "Org Chart",
      icon: menuIcon("dataflow"),
      onClick: () => openSettingsAndNavigate("/org-chart"),
    },
    {
      type: "divider",
    },
    {
      type: "button",
      label: "Settings",
      icon: menuIcon("adjustments"),
      onClick: handleOpenSettings,
    },
    {
      type: "divider",
    },
    organization.featureFlags.meetings && {
      type: "button",
      label: "Meeting Templates",
      icon: menuIcon("file04"),
      onClick: () => openSettingsAndNavigate("/templates"),
    },
    organization.featureFlags.meetings && {
      type: "link",
      label: "Get the Chrome Extension",
      icon: menuIcon("externalLink"),
      to: chromeExtensionUrl,
    },
    {
      type: "button",
      label: "Clear Cache",
      icon: menuIcon("refresh"),
      onClick: handleClearCache,
    },
    {
      type: "divider",
    },
    {
      type: "button",
      label: "Logout",
      icon: menuIcon("exit"),
      onClick: handleLogout,
    },
  ]) as DropdownOptionType[];

  return (
    <div
      className={classNames(
        "flex items-center min-w-0",
        menuIsCollapsed ? "flex-col gap-2" : "gap-1",
      )}
    >
      <div className="flex items-center min-w-0">
        <Dropdown options={options} gap={8}>
          <button
            aria-label="Organization menu"
            className={classNames(
              "flex cursor-pointer items-center min-w-0 text-left gap-2 text-gray-700 hover:bg-nav-item-active group rounded px-2 py-1",
              menuIsCollapsed && "justify-center",
            )}
          >
            {!menuIsCollapsed && (
              <>
                <span className="tf-header-sm truncate">
                  {organization.name}
                </span>
                <TopicflowIcon
                  type="chevronDown"
                  className="h-3 w-3 shrink-0 text-gray-500"
                />
              </>
            )}
          </button>
        </Dropdown>
      </div>
      <div className="flex items-center">
        {window.hijackedUser && isInternalImpersonation() && (
          <form action={window.hijackedUser.release_url} method="POST">
            <input
              type="hidden"
              name="csrfmiddlewaretoken"
              value={window.TF.csrf}
            />
            <input
              type="hidden"
              name="next"
              value={window.hijackedUser.next_url}
            />
            <Tooltip
              placement="right"
              text={`Release ${window.hijackedUser.user.name}`}
            >
              <button
                type="submit"
                aria-label={`Release ${window.hijackedUser.user.name}`}
                className={classNames(
                  "cursor-pointer flex items-center justify-center text-gray-500 text-sm hover:bg-nav-item-active hover:text-gray-600 shrink-0",
                  sidebarButtonClassName,
                )}
              >
                <TopicflowIcon
                  type="exit"
                  className={classNames("h-4 w-4", "shrink-0")}
                />
              </button>
            </Tooltip>
          </form>
        )}
      </div>
    </div>
  );
};
export default SidebarSettings;
